import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Style/GenerateMDReportAndLP.css';
import './Style/header.css';
import './Style/common.css';
// Import all SVG images
import s1 from './Image/gwd/generate_anim/s1.svg';
import s2 from './Image/gwd/generate_anim/s2.svg';
import s3 from './Image/gwd/generate_anim/s3.svg';
import s4 from './Image/gwd/generate_anim/s4.svg';
import comp_base from './Image/gwd/generate_anim/comp_base_1.svg';
import comp_head from './Image/gwd/generate_anim/comp_head.svg';
import comp_glasses from './Image/gwd/generate_anim/comp_glasses.svg';
import comp_smile from './Image/gwd/generate_anim/comp_smile.svg';
import comp_eyes from './Image/gwd/generate_anim/comp_eyes.svg';
import tablet2 from './Image/gwd/generate_anim/tablet2.svg';
import tablet1 from './Image/gwd/generate_anim/tablet1.svg';
import mobile from './Image/gwd/generate_anim/mobile.svg';
import laptop from './Image/gwd/generate_anim/laptop.svg';
import bg1 from './Image/gwd/generate_anim/bg1.svg';
import screen_content1 from './Image/gwd/generate_anim/screen_content1x.svg';
import screen_content2 from './Image/gwd/generate_anim/screen_content2_1.svg';
import bg3 from './Image/gwd/generate_anim/bg3.svg';
import bg2 from './Image/gwd/generate_anim/bg2.svg';
import screen_content3 from './Image/gwd/generate_anim/screen_content3.svg';
import bg4 from './Image/gwd/generate_anim/bg4.svg';
import screen_content5 from './Image/gwd/generate_anim/screen_content5.svg';
import screen_content4 from './Image/gwd/generate_anim/screen_content4.svg';
import screen_content6 from './Image/gwd/generate_anim/screen_content6.svg';
import screen_content7 from './Image/gwd/generate_anim/screen_content7.svg';
import screen_content8 from './Image/gwd/generate_anim/screen_content8.svg';
import load_bar from './Image/gwd/generate_anim/load_bar.svg';
import b1 from './Image/gwd/generate_anim/load_bar_piece.svg';
import b2 from './Image/gwd/generate_anim/load_bar_piece.svg';
import b3 from './Image/gwd/generate_anim/load_bar_piece.svg';
import b4 from './Image/gwd/generate_anim/load_bar_piece.svg';
import wall from "./Image/wall.jpg";
import { useSearchParams } from "react-router-dom";
const GenerateMDReportAndLP: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isGenerating, setIsGenerating] = useState<boolean>(true);
  const [showContinue, setShowContinue] = useState<boolean>(false);
  const [text, setText] = useState<string>('Generating your report...');
  const [waitText, setWaitText] = useState<string>('Continue button will appear once ready');
  const [opacity, setOpacity] = useState<number>(1);
  const [zoom, setZoom] = useState<number>(1);
  const [showLoadingBar, setShowLoadingBar] = useState<boolean>(true);
  const [searchParams] = useSearchParams();
  const reportNo = searchParams.get("ReportNo") || "0";
  const calledFrom = searchParams.get("CalledFrom") || "";
  const apiUrl =
    process.env.REACT_APP_DOCUMENT_LINK;
  // console.log("reportNo:", reportNo);
  // console.log("calledFrom:", calledFrom);
  useEffect(() => {
    let isMounted = true;
    let messageTimeouts: ReturnType<typeof setTimeout>[] = [];

    const fetchData = async () => {
      try {
        const schoolCode = sessionStorage.getItem('SchoolCode') || '';
        const familyCode = sessionStorage.getItem('FamilyCode') || '';
        const login = sessionStorage.getItem('Login') || '';

        if (!schoolCode || !familyCode || !login) {
          alert('Session expired. Please login again.');
          navigate('/login');
          return;
        }

        const response = await fetch(`${apiUrl}assessment/generate-report/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            school_code: schoolCode,
            family_code: familyCode,
            login: login,
            report_no: reportNo,
          }),
        });

        const data = await response.json();

        if (!isMounted) return;

        if (!data.success) {
          setText(`Error: ${data.error || 'Failed to generate report'}`);
          setWaitText('');
          setIsGenerating(false);
          setShowLoadingBar(false);
          setShowContinue(false);
          return;
        }

        // ============================================================
        // API COMPLETED SUCCESSFULLY
        // ============================================================

        setIsGenerating(false);
        setShowLoadingBar(false);
        setWaitText('');
        setShowContinue(true);

      } catch (error) {
        if (!isMounted) return;

        console.error('Error generating report:', error);

        setText('Error generating report. Please try again.');
        setWaitText('');
        setIsGenerating(false);
        setShowLoadingBar(false);
        setShowContinue(false);
      }
    };

    fetchData();

    // ================================================================
    // ANIMATED TEXT SEQUENCE
    // Keep changing messages until API finishes
    // ================================================================

    const userName = sessionStorage.getItem('FName');

    let subject = sessionStorage.getItem('Subject') || 'UK';

    if (subject.toLowerCase() === 'mathematics') {
      subject = 'Maths';
    }

    const messages = [
      'Processing collected information',
      'Calculating necessary activities',
      `Generating ${userName}'s lesson plan`,
      `Creating ${subject} Lesson Plan`,
    ];

    const showMessage = (index: number) => {
      if (!isMounted) return;

      setOpacity(0);
      setZoom(1.2);

      const fadeTimeout = setTimeout(() => {
        if (!isMounted) return;

        setText(messages[index].toUpperCase());
        setOpacity(1);
        setZoom(1);

        if (index === messages.length - 1) {
          setWaitText('Continue button will appear once ready');
          setShowLoadingBar(false);
        }

        // Continue cycling through messages
        const nextTimeout = setTimeout(() => {
          if (!isMounted) return;

          const nextIndex = (index + 1) % messages.length;
          showMessage(nextIndex);
        }, 4000);

        messageTimeouts.push(nextTimeout);

      }, 500);

      messageTimeouts.push(fadeTimeout);
    };

    // Start animation
    showMessage(0);

    // ================================================================
    // HANDLE WINDOW RESIZE
    // ================================================================

    const handleResize = () => {
      document.body.style.height = `${window.innerHeight - 40}px`;
      document.body.style.width = `${window.innerWidth}px`;
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      isMounted = false;

      messageTimeouts.forEach((timeout) => {
        clearTimeout(timeout);
      });

      window.removeEventListener('resize', handleResize);
    };

  }, [navigate, location]);

  const handleContinueClick = () => {
    const queryString = `?ReportNo=${encodeURIComponent(reportNo)}&CalledFrom=${encodeURIComponent(calledFrom)}`;
    navigate(`/MathsDoctor/Assessment/AssessmentComplete${queryString}`);
  };

  return (
    <div
      style={{
        backgroundImage: `url(${wall})`,
        height: "100vh",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <a href="/MathsDoctor/Assessment/GenerateMDReportAndLP" className="back-to-top">Back to Top</a>
      <div id="main_top_container">
        <div id="header">
          <div className="section_div"></div>
        </div>
        <div id="math_generate_title_bar" className="section_container section_title_bar">
          <div id="bt_math_generate" className="section_div bar_section">
            <p className="bar_title"></p>
          </div>
        </div>
      </div>
      <div id="gen_container">
        <div id="generate_anim" className="htmlNoPages">
          <img src={s1} className="gwd-img-gkwh gwd-gen-1gmlgwdanimation" id="s1" alt="" />
          <img src={s2} className="gwd-img-ah1j gwd-gen-1mo8gwdanimation" id="s2" alt="" />
          <img src={s3} className="gwd-img-1wi4 gwd-gen-u87dgwdanimation" id="s3" alt="" />
          <img src={s4} className="gwd-img-1nih gwd-gen-rfodgwdanimation" id="s4" alt="" />
          <img src={comp_base} className="gwd-img-3odo" id="comp_base" alt="" />
          <img src={comp_head} className="gwd-img-1mvb gwd-gen-tapmgwdanimation" id="comp_body" alt="" />
          <img src={comp_glasses} className="gwd-img-2kvm gwd-gen-5n3jgwdanimation" id="comp_glasses" alt="" />
          <img src={comp_smile} className="gwd-img-862d gwd-gen-hd8qgwdanimation" id="comp_smile" alt="" />
          <img src={comp_eyes} className="gwd-img-1cwu gwd-gen-1n6ogwdanimation" id="comp_eyes" alt="" />
          <img src={tablet2} className="gwd-img-unam gwd-gen-1cr4gwdanimation" id="tablet2" alt="" />
          <img src={tablet1} className="gwd-img-11g3 gwd-gen-yw3ogwdanimation" id="tablet1" alt="" />
          <img src={mobile} className="gwd-img-tys5 gwd-gen-1pddgwdanimation" id="mobile" alt="" />
          <img src={laptop} className="gwd-img-1mll gwd-gen-cb3rgwdanimation" id="laptop" alt="" />
          <img src={bg1} className="gwd-img-479c gwd-gen-jggmgwdanimation" id="screen1" alt="" />
          <img src={screen_content1} className="gwd-img-16ck gwd-gen-1yrlgwdanimation" id="screen_content1" alt="" />
          <img src={screen_content2} className="gwd-img-aars gwd-gen-lbaxgwdanimation" id="screen_content2" alt="" />
          <img src={bg3} className="gwd-img-13am gwd-gen-2cfygwdanimation" id="screen3" alt="" />
          <img src={bg2} className="gwd-img-1odr gwd-gen-tou3gwdanimation" id="screen2" alt="" />
          <img src={screen_content3} className="gwd-img-14mr gwd-gen-b3pigwdanimation" id="screen_content3" alt="" />
          <img src={bg4} className="gwd-img-1k1q" id="screen4" alt="" />
          <img src={screen_content5} className="gwd-img-1pqv gwd-gen-14n9gwdanimation" alt="" />
          <img src={screen_content4} className="gwd-img-j6e0 gwd-gen-1jvcgwdanimation" alt="" />
          <img src={screen_content6} className="gwd-img-5051 gwd-gen-m65egwdanimation" alt="" />
          <img src={screen_content7} className="gwd-img-1o76 gwd-gen-siungwdanimation" alt="" />
          <img src={screen_content8} className="gwd-img-y238 gwd-gen-1jz2gwdanimation" alt="" />
          {showLoadingBar && (
            <>
              <img src={load_bar} className="gwd-img-oa87" id="load_bar" alt="" />
              <img src={b1} className="gwd-img-gk29 gwd-gen-x5ipgwdanimation" id="b1" alt="" />
              <img src={b2} className="gwd-img-1tf4 gwd-gen-1m9cgwdanimation" id="b2" alt="" />
              <img src={b3} className="gwd-img-w9sx gwd-gen-ypldgwdanimation" id="b3" alt="" />
              <img src={b4} className="gwd-img-13ao gwd-gen-zzdfgwdanimation" id="b4" alt="" />
            </>
          )}
        </div>
        <div id="generate_text_container" style={{ textAlign: "center" }}>
          <h2
            id="gtext"
            style={{

              transition: "opacity 0.6s ease-in-out, transform 0.6s ease-in-out",
              opacity: opacity,
              transform: `scale(${zoom})`,
            }}
          >
            {text}
          </h2>
          {isGenerating && (
            <div id="" style={{ color: "white", marginTop: "100px" }}>
              {waitText}
            </div>
          )}
          {showContinue && (
            <div>
              <button
                onClick={handleContinueClick}
                className="linkbtnContinue"
                style={{
                  height: "60px",
                  backgroundColor: "darkorange",
                  width: "200px",
                  display: "inline-table",
                  marginTop: "130px",
                  color: "white",
                  fontSize: "20px",

                  cursor: "pointer",
                  fontWeight: "bold",

                }}
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateMDReportAndLP;
